import { useTranslate } from '@tolgee/react'
import { useYardStores } from '@yard/AppProvider'
import { GlobalAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/global-allocation-rules.view-store'
import { YardViewStore } from '@yard/pages/YardSetup/stores/yard.view-store'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { CarrierVisitAllocationRulesViewStore } from '../../../../stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { CarrierVisitRulesDrawerHeader } from '../../../molecules/AllocationRules/CarrierVisitAllocationRules/CarrierVisitRulesDrawerHeader'
import { UnallocatedContainersAccordion } from '../../../molecules/AllocationRules/UnallocatedContainersAccordion'
import { YardLeftDrawer } from '../../../molecules/YardLeftDrawer'
import { AllocationRuleAccordion } from '../AllocationRuleAccordion'
import { AllocationRulesDialogs } from '../AllocationRuleDialogs'
import { CarrierVisitCopyRulesDialog } from '../CarrierVisitCopyRulesDialog'

interface Props {
  yardViewStore: YardViewStore
  carrierVisitAllocationRulesViewStore: CarrierVisitAllocationRulesViewStore
  globalAllocationRulesViewStore: GlobalAllocationRulesViewStore
}
export const CarrierVisitAllocationRulesDrawer: FC<Props> = observer(props => {
  const { yardViewStore, carrierVisitAllocationRulesViewStore, globalAllocationRulesViewStore } =
    props
  const { allocationRuleStore } = useYardStores()
  const { t } = useTranslate()

  const handleFromLibrary = async () => {
    await allocationRuleStore.loadTemplateRules()
    carrierVisitAllocationRulesViewStore.toggleTemplateDialog()
  }

  const handleCopyFromVisit = () => {
    carrierVisitAllocationRulesViewStore.toggleCopyFromVisitDialog()
  }

  return (
    <>
      <YardLeftDrawer
        open={
          carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen &&
          yardViewStore.isViewMode
        }
        onClose={() => carrierVisitAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)}
      >
        <CarrierVisitRulesDrawerHeader
          carrierName={carrierVisitAllocationRulesViewStore.selectedCarrierVisit?.name || ''}
          carrierType={carrierVisitAllocationRulesViewStore.selectedCarrierVisit?.carrierType}
          carrierVisitDirection={carrierVisitAllocationRulesViewStore.selectedCarrierVisitDirection}
          onCreateNewRule={() => carrierVisitAllocationRulesViewStore.toggleFormDialog()}
          onImportFromLibrary={handleFromLibrary}
          onCopyFromVisit={handleCopyFromVisit}
        />

        <AllocationRuleAccordion
          droppableId='carrierVisitAllocationRulesList'
          title={t('carrierVisitRules', 'Carrier Visit Rules')}
          tooltip={t(
            'carrierVisitRulesTooltip',
            'Carrier visit rules are applied only to specific carrier visits. These rules have higher priority than global rules.',
          )}
          isEditable
          allocationRules={carrierVisitAllocationRulesViewStore.selectedCarrierVisitAllocationRules}
          capacityByRule={carrierVisitAllocationRulesViewStore.capacityByRule}
          matchingCountByRule={carrierVisitAllocationRulesViewStore.matchingCountByRule}
          onReorder={(sourceIndex, destinationIndex) =>
            carrierVisitAllocationRulesViewStore.reorderAllocationRule(
              sourceIndex,
              destinationIndex,
            )
          }
          hoveredIndex={carrierVisitAllocationRulesViewStore.hoveredRuleIndex}
          onHover={index => carrierVisitAllocationRulesViewStore.setHoveredRuleIndex(index)}
          onEdit={index => carrierVisitAllocationRulesViewStore.toggleFormDialog(index)}
          onDelete={index => carrierVisitAllocationRulesViewStore.deleteAllocationRule(index)}
        />

        <AllocationRuleAccordion
          droppableId='globalAllocationRulesList'
          title={t('globalRules', 'Global rules')}
          tooltip={t(
            'globalRulesTooltip',
            'Global rules are always working to organize your yard. These can be overridden when planning specific visits.',
          )}
          isEditable={false}
          allocationRules={globalAllocationRulesViewStore.orderedAllocationRules}
          capacityByRule={carrierVisitAllocationRulesViewStore.capacityByRule}
          matchingCountByRule={carrierVisitAllocationRulesViewStore.matchingCountByRule}
          hoveredIndex={globalAllocationRulesViewStore.hoveredRuleIndex}
          onHover={index => globalAllocationRulesViewStore.setHoveredRuleIndex(index)}
        />

        <UnallocatedContainersAccordion
          unallocatedJourneys={carrierVisitAllocationRulesViewStore.unallocatedContainers}
        />
      </YardLeftDrawer>
      <AllocationRulesDialogs
        yardViewStore={yardViewStore}
        allocationRulesViewStore={carrierVisitAllocationRulesViewStore}
      />
      <CarrierVisitCopyRulesDialog
        allocationRulesViewStore={carrierVisitAllocationRulesViewStore}
      />
    </>
  )
})
